/*
 * Copyright (c) 2021-2026.  atagon GmbH.  All rights reserved.
 *
 * This file is part of the atagon codebase and may not be copied,
 *  distributed, or modified without explicit written permission from
 * atagon GmbH. https://www.atagon.com
 */

import React, {useState} from 'react';
import Device from "toolbox/device";
import Strings from 'toolbox/strings';
import {
    autoUpdate,
    flip,
    FloatingPortal,
    offset,
    shift,
    useDismiss,
    useFloating,
    useFocus,
    useHover,
    useInteractions,
    useRole
} from '@floating-ui/react';


export function Tooltip({
                            delay = 200,
                            children,
                            isActive = true,
                            text,
                            className,
                            onClick
                        }) {
    const [isOpen, setIsOpen] = useState(false);

    const {refs, floatingStyles, context} = useFloating({
        open: isOpen,
        onOpenChange: setIsOpen,
        placement: "bottom",
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(8),
            flip({fallbackAxisSideDirection: "start"}),
            shift({padding: 5})
        ]
    });

    const hover = useHover(context, {
        move: false,
        delay: {open: delay, close: 0},
        enabled: isActive
    });

    const focus = useFocus(context, {enabled: isActive});
    const dismiss = useDismiss(context);
    const role = useRole(context, {role: 'tooltip'});

    const {getReferenceProps, getFloatingProps} = useInteractions([
        hover,
        focus,
        dismiss,
        role
    ]);

    const isMobile = Device.isMobile();
    const shouldDisable = Strings.isEmpty(text) || isMobile || !isActive;

    if (shouldDisable) {
        return (
            <div className={className} onClick={onClick}>
                {children}
            </div>
        );
    }

    const safeText = Array.isArray(text) ? text.join('') : (text || "");

    return (
        <>
            <div
                ref={refs.setReference}
                {...getReferenceProps()}
                className={className}
                onClick={onClick}
            >
                {children}
            </div>

            {isOpen && (
                <FloatingPortal>
                    <div
                        ref={refs.setFloating}
                        style={floatingStyles}
                        {...getFloatingProps()}
                        className="z-50 pointer-events-none"
                    >
                        <div
                            className="bg-black text-white text-sm rounded-lg shadow-lg p-2 opacity-90 transition-all
                                       max-w-[200px] truncate"
                        >
                            {safeText}
                        </div>
                    </div>
                </FloatingPortal>
            )}
        </>
    );
}

export default Tooltip;
